import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';

import {
  Button,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';

import { TextField } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/lab';

import AdapterDateFns from '@mui/lab/AdapterDateFns';

import moment from 'moment';
import { useHistory, useParams } from 'react-router';
import Input from '../../../components/Input';
import { useAxios } from '../../../utils/hooks';
import getValidationErrors from '../../../utils/getValidationErrors';
import { Form as FormModel } from '../../../models/form';
import { Record } from '../../../models/record';
import Loader from '../../../components/Loader';
import CustomCheckbox from '../../../components/Checkbox';
import Snackbar, { AlertComponentProps } from '../../../components/Snackbar';
import Signatures from '../../../components/Signatures';
import { SignatureForm } from '../../../models/signature-form';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    background: 'white',
    padding: theme.spacing(3),
    margin: theme.spacing(3),
    marginBottom: theme.spacing(10),
  },
  formTitles: {
    fontWeight: 'bolder',
  },
  formSubTitles: {
    fontWeight: 'normal',
  },
  formControl: {
    margin: theme.spacing(2, 0),
  },
  divider: {
    margin: theme.spacing(2),
  },
  groupTitle: {
    display: 'flex',
    '& span': {
      fontWeight: 'bolder',
    },
  },
  dateGrid: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const PsychiatricForm: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const api = useAxios();
  const [form, setForm] = useState<FormModel | null>(null);
  const [record, setRecord] = useState<Record | null>(null);
  const [signatures, setSignatures] = useState<SignatureForm[]>([]);
  const [date, setDate] = useState(new Date());
  const [date_social, setDateSocial] = useState(new Date());
  const [patient_birth, setPatientBirth] = useState(new Date());

  const { id, formId } = useParams<{ id: string; formId?: string }>();

  const formRef = useRef<FormHandles>(null);

  const [alert, setAlert] = useState<
    AlertComponentProps & { isActive: boolean }
  >({
    isActive: false,
    type: 'info',
    message: 'none',
  });

  useEffect(() => {
    async function getRecordData() {
      try {
        const recordResponse = await api.current?.get(`/records/${id}`);
        setRecord(recordResponse?.data);

        if (formId) {
          const formResponse = await api.current?.get(`/forms/${formId}`);
          if (formResponse?.data) {
            setDate(
              formResponse?.data.answers.date
                ? moment(formResponse?.data.answers.date).toDate()
                : new Date(),
            );
          }
          if (formResponse?.data) {
            setDateSocial(
              formResponse?.data.answers.date_socal
                ? moment(formResponse?.data.answers.date_social).toDate()
                : new Date(),
            );
          }
          if (formResponse?.data) {
            setPatientBirth(
              formResponse?.data.answers.patient_birth
                ? moment(formResponse?.data.answers.patient_birth).toDate()
                : new Date(),
            );
          }
          setForm(formResponse?.data);

          if (
            formResponse?.data.answers &&
            formResponse?.data.answers.signatures
          ) {
            setSignatures(formResponse?.data.answers.signatures);
          }
        }
      } catch (error) {
        setAlert({
          isActive: true,
          type: 'error',
          message: 'Erro ao carregar prontuário.',
        });
      } finally {
        setLoading(false);
      }
    }

    getRecordData();
  }, [api, id, formId]);

  const resetAlert = () => {
    setAlert({
      isActive: false,
      type: 'info',
      message: 'none',
    });
  };

  const handleSubmit = useCallback(
    async (data: any) => {
      try {
        setLoading(true);
        resetAlert();
        formRef.current?.setErrors({});

        // const schema = Yup.object().shape({
        //   content: Yup.string().required('Insira uma nota'),
        // });

        // await schema.validate(data, { abortEarly: false });
        data.date = moment(date).format('YYYY-MM-DD');
        data.patient_birth = moment(patient_birth).format('YYYY-MM-DD');
        if (data.date_social)
          data.social_date = moment(date).format('YYYY-MM-DD');

        const formRequest = {
          type: 'hosting',
          title: 'Acolhimento',
          patient: record?.patient._id,
          record: record?._id,
          answers: { ...data, signatures },
        };

        if (formId) {
          await api.current?.patch(`/forms/${formId}`, formRequest);
        } else {
          await api.current?.post('/forms', formRequest);
        }

        history.push(`/prontuarios/${id}`);
      } catch (error) {
        setLoading(false);
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
        } else {
          setAlert({
            isActive: true,
            type: 'error',
            message: 'Erro ao enviar ficha.',
          });
        }
      }
    },
    [api, record, patient_birth, history, formId, date, id, signatures],
  );

  return (
    <>
      <Loader loading={loading} />

      {!loading && (
        <Form
          id="form"
          ref={formRef}
          initialData={form?.answers || { record: record?.number }}
          onSubmit={handleSubmit}
          className={classes.root}
        >
          <FormControl fullWidth className={classes.formControl}>
            <Typography
              variant="h5"
              component="h1"
              align="center"
              className={classes.formTitles}
            >
              ACOLHIMENTO
            </Typography>

            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    label="Data"
                    inputFormat="dd/MM/yyyy"
                    value={date_social}
                    onChange={(d) => setDateSocial(d as Date)}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={1} />

              <Grid item xs={4}>
                <Input
                  id="hour"
                  label="Horário"
                  name="hour"
                  placeholder="___:___"
                />
              </Grid>

              <Grid item xs={4}>
                <Input disabled id="record" label="Prontuário" name="record" />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Identificação
                </FormLabel>
              </Grid>
              <Grid item xs={12}>
                <Input id="patient_name" label="Nome" name="patient_name" />
              </Grid>

              <Grid item xs={6}>
                <Input id="patient_age" label="Idade" name="patient_age" />
              </Grid>

              <Grid item xs={2} />

              <Grid item xs={4}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    label="Data de Nascimento"
                    inputFormat="dd/MM/yyyy"
                    value={patient_birth}
                    onChange={(d) => setPatientBirth(d as Date)}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={8}>
                <Input id="patient_mother" label="Mãe" name="patient_mother" />
              </Grid>

              <Grid item xs={4}>
                <Input
                  id="patient_mother_age"
                  label="Idade"
                  name="patient_mother_age"
                />
              </Grid>

              <Grid item xs={8}>
                <Input id="patient_father" label="Pai" name="patient_father" />
              </Grid>

              <Grid item xs={4}>
                <Input
                  id="patient_father_age"
                  label="Idade"
                  name="patient_father_age"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="interview_members"
                  label="Quem compareceu a entrevista"
                  name="interview_members"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="patient_relationship"
                  label="Grau de parentesco com o/a paciente"
                  name="patient_relationship"
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>Demanda</FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="interview_type"
                  label="Tipo de Atendimento"
                  name="interview_type"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="motive"
                  label="Motivo da procura/queixa"
                  name="motive"
                  isMultiline
                  rows={5}
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="schedule"
                  label="Disponibilidade de horário para atendimento"
                  name="schedule"
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Dados Sociais e Econômicos
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Typography className={classes.formTitles}>
                  Condições de Moradia
                </Typography>
              </Grid>

              <Grid item xs={3}>
                <CustomCheckbox
                  name="own_house"
                  value="own_house"
                  label="Própria"
                />
              </Grid>

              <Grid item xs={3}>
                <CustomCheckbox
                  name="lend_house"
                  value="lend_house"
                  label="Cedida"
                />
              </Grid>

              <Grid item xs={3}>
                <CustomCheckbox
                  name="rent_house"
                  value="rent_house"
                  label="Alugada"
                />
              </Grid>

              <Grid item xs={3}>
                <CustomCheckbox
                  name="financed_house"
                  value="financed_house"
                  label="Financiada"
                />
              </Grid>

              <Grid item xs={12}>
                <Input id="condominium" label="Condomínio" name="condominium" />
              </Grid>

              <Grid item xs={12}>
                <Typography className={classes.formTitles}>
                  Despesas Mensais Básicas
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Input id="food" label="Alimentação" name="food" />
              </Grid>

              <Grid item xs={3}>
                <Input id="water" label="Água" name="water" />
              </Grid>

              <Grid item xs={3}>
                <Input id="light" label="Luz" name="light" />
              </Grid>

              <Grid item xs={6}>
                <Input
                  id="internet"
                  label="Tel. Fixo/ Internet / TV a cabo"
                  name="internet"
                />
              </Grid>

              <Grid item xs={6}>
                <Input id="phone" label="Tel. Celular" name="phone" />
              </Grid>

              <Grid item xs={12}>
                <Typography className={classes.formTitles}>
                  Situação Escolar
                </Typography>
              </Grid>

              <Grid item xs={8}>
                <Input id="school" label="Escola" name="school" />
              </Grid>

              <Grid item xs={4}>
                <Input id="school_hour" label="Turno" name="school_hour" />
              </Grid>

              <Grid item xs={6}>
                <CustomCheckbox
                  name="public_school"
                  value="public_school"
                  label="Pública Municipal"
                />
              </Grid>

              <Grid item xs={6}>
                <CustomCheckbox
                  name="public_private"
                  value="public_private"
                  label="Privada com bolsa"
                />
              </Grid>

              <Grid item xs={6}>
                <CustomCheckbox
                  name="public_state"
                  value="public_state"
                  label="Pública Estadual"
                />
              </Grid>

              <Grid item xs={6}>
                <CustomCheckbox
                  name="public_private_paid"
                  value="public_private_paid"
                  label="Privada sem bolsa"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography className={classes.formTitles}>
                  Condições de Mobilidade
                </Typography>
              </Grid>

              <Grid item xs={3}>
                <CustomCheckbox name="bus" value="bus" label="Ônibus" />
              </Grid>

              <Grid item xs={3}>
                <CustomCheckbox
                  name="little_bus"
                  value="little_bus"
                  label="Lotação"
                />
              </Grid>

              <Grid item xs={3}>
                <CustomCheckbox name="car" value="car" label="Carro" />
              </Grid>

              <Grid item xs={3}>
                <CustomCheckbox
                  name="motorcycle"
                  value="motorcycle"
                  label="Moto"
                />
              </Grid>

              <Grid item xs={12}>
                <span>
                  Necessita de transporte para comparecer ao atendimento
                </span>
              </Grid>

              <Grid item xs={6}>
                <CustomCheckbox name="yes_ride" value="yes_ride" label="Sim" />
              </Grid>

              <Grid item xs={6}>
                <CustomCheckbox name="no_ride" value="no_ride" label="Não" />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="ride_expenses"
                  label="Despesa mensal com transporte"
                  name="ride_expenses"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography className={classes.formTitles}>
                  Condições de Acesso a Cultura, Esporte e Lazer
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="culture"
                  label="Condições de Acesso a Cultura, Esporte e Lazer"
                  name="culture"
                  isMultiline
                  rows={5}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography className={classes.formTitles}>
                  Condições de Saúde
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="health_plan"
                  label="Plano de Saúde"
                  name="health_plan"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="family_health"
                  label="Familiar em tratamento de saúde, com doenças crônicas ou com deficiência"
                  name="family_health"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="medicines"
                  label="Medicamentos de uso contínuo"
                  name="medicines"
                />
              </Grid>

              <Grid item xs={12}>
                <Input id="treatments" label="Tratamentos" name="treatments" />
              </Grid>

              <Grid item xs={12}>
                <Typography className={classes.formTitles}>
                  Outras despesas
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Input
                  id="loan"
                  label="Empréstimos"
                  name="loan"
                  placeholder="R$"
                />
              </Grid>

              <Grid item xs={6}>
                <Input
                  id="loan_end"
                  label="Previsão de data de término"
                  name="loan_end"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="loan_others"
                  label="Outros"
                  name="loan_others"
                  isMultiline
                  rows={3}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography className={classes.formTitles}>
                  Composição da Renda Familiar
                </Typography>
              </Grid>

              <Grid item xs={8}>
                <Input
                  id="family_income_origin_1"
                  label="Origem"
                  name="family_income_origin_1"
                />
              </Grid>

              <Grid item xs={4}>
                <Input
                  id="family_income_1"
                  label="Valor"
                  name="family_income_1"
                />
              </Grid>

              <Grid item xs={8}>
                <Input
                  id="family_income_origin_2"
                  label="Origem"
                  name="family_income_origin_2"
                />
              </Grid>

              <Grid item xs={4}>
                <Input
                  id="family_income_2"
                  label="Valor"
                  name="family_income_2"
                />
              </Grid>

              <Grid item xs={8}>
                <Input
                  id="family_income_origin_3"
                  label="Origem"
                  name="family_income_origin_3"
                />
              </Grid>

              <Grid item xs={4}>
                <Input
                  id="family_income_3"
                  label="Valor"
                  name="family_income_3"
                />
              </Grid>

              <Grid item xs={8}>
                <Input
                  id="family_income_origin_4"
                  label="Origem"
                  name="family_income_origin_4"
                />
              </Grid>

              <Grid item xs={4}>
                <Input
                  id="family_income_4"
                  label="Valor"
                  name="family_income_4"
                />
              </Grid>

              <Grid item xs={8}>
                <Input
                  id="family_income_origin_5"
                  label="Origem"
                  name="family_income_origin_5"
                />
              </Grid>

              <Grid item xs={4}>
                <Input
                  id="family_income_5"
                  label="Valor"
                  name="family_income_5"
                />
              </Grid>

              <Grid item xs={8} />

              <Grid item xs={4}>
                <Input
                  id="total_income_value"
                  label="Total"
                  name="total_income_value"
                />
              </Grid>

              <Grid item xs={12}>
                <span>
                  Receberá auxílio financeiro de terceiros para custear o
                  tratamento
                </span>
              </Grid>

              <Grid item xs={6}>
                <CustomCheckbox
                  name="yes_financial_help"
                  value="yes_financial_help"
                  label="Sim"
                />
              </Grid>

              <Grid item xs={6}>
                <CustomCheckbox
                  name="no_financial_help"
                  value="no_financial_help"
                  label="Não"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="yes_financial_help_specify"
                  label="De quem?"
                  name="yes_financial_help_specify"
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  História Familiar / Dados Pregressos
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="family_history"
                  label="História Familiar / Dados Pregressos"
                  name="family_history"
                  isMultiline
                  rows={5}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Parecer da Assistente Social
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="social_assistant"
                  label="Parecer da Assistente Social"
                  name="social_assistant"
                  isMultiline
                  rows={5}
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="attendence_possibilities"
                  label="Possibilidade de outros atendimentos"
                  name="attendence_possibilities"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="amount_suggestion"
                  label="Sugestão de Valor"
                  name="amount_suggestion"
                  placeholder="R$"
                />
              </Grid>

              <Grid item xs={12}>
                <span>Permaneceu na Instituição</span>
              </Grid>

              <Grid item xs={2}>
                <CustomCheckbox
                  name="yes_stayed"
                  value="yes_stayed"
                  label="Sim"
                />
              </Grid>

              <Grid item xs={2}>
                <CustomCheckbox
                  name="no_stayed"
                  value="no_stayed"
                  label="Não"
                />
              </Grid>

              <Grid item xs={8}>
                <Input
                  id="no_stayed_specify"
                  label="Se não, encaminhado para"
                  name="no_stayed_specify"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="observations"
                  label="Observações"
                  name="observations"
                  isMultiline
                  rows={5}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formTitles}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Assinaturas
                </FormLabel>
              </Grid>

              <Grid item xs={12} justify="center">
                <Signatures
                  formAnswers={form ? form.answers : {}}
                  onSignature={setSignatures}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={4} />

              <Grid item xs={2} className={classes.dateGrid}>
                <span>Porto Alegre,</span>
              </Grid>

              <Grid item xs={1}>
                <Input id="day" label="Dia" name="day" />
              </Grid>

              <Grid item xs={1} className={classes.dateGrid}>
                de
              </Grid>

              <Grid item xs={1}>
                <Input id="month" label="Mês" name="month" />
              </Grid>

              <Grid item xs={1} className={classes.dateGrid}>
                de
              </Grid>

              <Grid item xs={2}>
                <Input id="year" label="Ano" name="year" />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formTitles}>
            <Grid container spacing={1} justify="center">
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => history.push(`/prontuarios/${id}`)}
                >
                  Voltar
                </Button>
              </Grid>

              <Grid item>
                <Button type="submit" color="primary" variant="contained">
                  Enviar
                </Button>
              </Grid>
            </Grid>
          </FormControl>
        </Form>
      )}

      {alert.isActive && <Snackbar {...alert} />}
    </>
  );
};

export default PsychiatricForm;
